import { useEffect, useMemo, useState } from "react";
import {
  SPORTS,
  GENDERS,
  TIERS,
  REGIONS_BY_TIER,
  NCAA_CONFERENCE_GROUPS,
  governingBodiesFor,
  subDivisionsFor,
  classOptionsFor,
  loadStateAssociations,
  ANY_LEAGUE_BODY,
  buildCachePartnerBody,
} from "../taxonomy";
import { fetchCachePartners } from "../services/cacheShareServices";
import { displayTeamName, stripSeasonSuffix } from "../../../../utils/teamNames";

// The cascade's field logic (option lists, which steps apply, and the reset
// rules where every upstream change clears the picks below it), shared by
// CascadePicker, the admin CacheShare rail and the Add player modal.
export const useCascadeFields = (filters, onFiltersChange) => {
  const { sport, gender, tier, region, body, subDivision } = filters;

  // CACHE Partner programs are admin-managed and fetched per gender.
  const [cachePartners, setCachePartners] = useState([]);
  useEffect(() => {
    const g = gender?.value;
    if (!g) {
      setCachePartners([]);
      return undefined;
    }
    let cancelled = false;
    fetchCachePartners({ gender: g }).then((list) => {
      if (!cancelled) setCachePartners(list || []);
    });
    return () => {
      cancelled = true;
    };
  }, [gender]);

  const bodyOptions = useMemo(() => {
    let list = governingBodiesFor({ tier: tier?.value, region: region?.value, gender: gender?.value, sport: sport?.value });
    if (tier?.value === "hs" && cachePartners.length) list = [...cachePartners.map(buildCachePartnerBody), ...list];
    if (tier?.value === "hs" || tier?.value === "pro") list = [ANY_LEAGUE_BODY, ...list];
    // A league picked through "Find any league" is not in the static
    // taxonomy: keep it selectable so the select shows its name.
    if (body?.catalogue && !list.some((b) => b.value === body.value)) list = [body, ...list];
    return list;
  }, [tier, region, gender, sport, cachePartners, body]);

  // State associations load from Cerebro's catalogue; re-derive once they land.
  const [statesTick, setStatesTick] = useState(0);
  useEffect(() => {
    if (body?.value !== "state_hs") return undefined;
    let cancelled = false;
    loadStateAssociations(gender?.value).then(() => {
      if (!cancelled) setStatesTick((t) => t + 1);
    });
    return () => {
      cancelled = true;
    };
  }, [body?.value, gender?.value]);
  const subDivisionOptions = useMemo(
    () => (body ? subDivisionsFor({ body, gender: gender?.value }) : []),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [body, gender, statesTick],
  );

  const set = (patch) => onFiltersChange({ ...filters, ...patch });
  const findBy = (list, v) => list.find((i) => String(i.value) === v) || null;
  const regionOptions = REGIONS_BY_TIER[tier?.value] || [];
  const conferenceOptions = NCAA_CONFERENCE_GROUPS.flatMap((g) => g.options);

  return {
    set,
    bodyOptions,
    subDivisionOptions,
    regionOptions,
    classOptions: classOptionsFor(subDivision),
    genderReady: !!sport && (!sport.hasGender || !!gender),
    showRegionStep: tier?.value === "pro" || tier?.value === "hs",
    showSubDivisionStep: !!body && subDivisionOptions.length > 0,
    showClassStep: subDivision?.classes?.length > 0,
    showConferenceStep: tier?.value === "college" && body?.value === "ncaa",
    onSport: (v) => set({ sport: findBy(SPORTS, v), gender: null, tier: null, region: null, body: null, subDivision: null, conference: null, teamSearch: "" }),
    onGender: (v) => set({ gender: findBy(GENDERS, v), tier: null, region: null, body: null, subDivision: null, conference: null, teamSearch: "" }),
    onTier: (v) => set({ tier: findBy(TIERS, v), region: null, body: null, subDivision: null, conference: null, teamSearch: "" }),
    onRegion: (v) => set({ region: findBy(regionOptions, v), body: null, subDivision: null, conference: null, teamSearch: "" }),
    onBody: (v) => set({ body: findBy(bodyOptions, v), subDivision: null, conference: null, teamSearch: "" }),
    onSubDivision: (v) => set({ subDivision: findBy(subDivisionOptions, v), classDivision: null, teamSearch: "" }),
    onClassDivision: (v) => {
      const cls = classOptionsFor(subDivision).find((c) => c.name === v);
      set({ classDivision: v ? { value: v, name: v, cerebroSports: cls?.cerebroSports || null } : null, teamSearch: "" });
    },
    onConference: (v) => set({ conference: findBy(conferenceOptions, v), teamSearch: "" }),
    onTeamSearch: (v) => set({ teamSearch: v }),
  };
};

// A place before a name ("Oakland" before "Soldiers"), unless the name
// already starts with it ("Oakland Soldiers", never "Oakland Oakland
// Soldiers").
const withPlace = (place, name) => {
  const where = String(place || "").trim();
  const what = String(name || "").trim();
  if (!where || what.toLowerCase() === where.toLowerCase() || what.toLowerCase().startsWith(`${where.toLowerCase()} `)) return what || where;
  return `${where} ${what}`.trim();
};

// Team names for lists and headers, by the one display rule
// (utils/teamNames, D15: the row's displayName, else its name with the age
// group its league key names, "Oakland Soldiers 16U", never a season CACHE
// added): CACHE Partner teams as named, Cerebro programs after their city
// and SportsRadar teams after their market, each unless the name already
// starts with it. Display only; rows stay keyed by id and league.
export const teamDisplayName = (t) => {
  if (!t) return "";
  const name = String(displayTeamName(t) || t.name || "").trim();
  if (t.source === "cache_teams") return name;
  if (t.source === "cerebro") return withPlace(t.city, name);
  return withPlace(t.market, name);
};

// A picker row's latest season (D15: never a year beside the name):
// the row's own season ("2025-26", or a competition's "2026"), else the
// academic year its latest event falls in (Aug 1 to Jul 31). "" without one.
export const latestSeasonOf = (t) => {
  const own = t?.season == null ? "" : String(t.season).trim();
  if (own) return own;
  const m = /^(\d{4})-(\d{2})/.exec(String(t?.latestEvent?.startDate || ""));
  if (!m) return "";
  const start = Number(m[2]) >= 8 ? Number(m[1]) : Number(m[1]) - 1;
  return `${start}-${String(start + 1).slice(2)}`;
};

// Two squads of one name active in the same season are both kept
// (team_names.collapse_season_duplicates) and read apart by the place the
// server names for each ("Oakland, CA", D15). "" for any other row.
export const twinNoteOf = (t) => String(t?.disambiguator || "").trim();

// The subline under a picker row: its conference or league (without the
// season CACHE once added to league labels), the place that tells a
// same-named squad apart, and "Latest season 2025-26".
export const teamRowSubline = (t) => {
  const league = stripSeasonSuffix(String(t?.conference || t?.leagueName || t?.location || "")).trim();
  const twin = twinNoteOf(t);
  const season = latestSeasonOf(t);
  return [league, twin.toLowerCase() !== league.toLowerCase() && twin, season && `Latest season ${season}`].filter(Boolean).join(" · ");
};

// "Latest event on file: Nike EYBL - Peach Jam (2025-07-17)", the hover
// title of a row's season; undefined without an event.
export const latestEventTitle = (t) =>
  t?.latestEvent?.name ? `Latest event on file: ${t.latestEvent.name} (${t.latestEvent.startDate || "no date"})` : undefined;

// A typed search matches the row's names and the name it shows ("soldiers
// 16u" finds Oakland Soldiers 16U).
export const filterTeams = (teams, query) => {
  const q = (query || "").trim().toLowerCase();
  if (!q) return teams;
  return teams.filter((t) => `${t.market || t.city || ""} ${t.name || ""} ${teamDisplayName(t)} ${t.alias || ""}`.toLowerCase().includes(q));
};

// "Men / Boys": the Figma spells the gender with a spaced slash.
export const spacedSlash = (s) => String(s || "").replace(/\s*\/\s*/, " / ");

// Coming-soon options keep their label with a plain suffix.
export const optionLabel = (o) => `${o.name}${o.status && o.status !== "live" ? " (coming soon)" : ""}`;

// The cascade's select fields as data, so the admin rail (stacked steps)
// and the Add player modal (two-column grid) render the same fields. A
// field is null while it does not apply.
export const cascadeFieldSpecs = (f, filters) => {
  const { sport, gender, tier, region, body, subDivision, classDivision, conference } = filters;
  const college = tier?.value === "college";
  return {
    sport: { label: "Sport", value: sport?.value, placeholder: "Pick a sport", options: SPORTS, onChange: f.onSport },
    gender: sport?.hasGender
      ? { label: "Gender", value: gender?.value, placeholder: "Pick a gender", options: GENDERS.map((g) => ({ ...g, name: spacedSlash(g.name) })), onChange: f.onGender }
      : null,
    tier: f.genderReady ? { label: "Tier", value: tier?.value, placeholder: "Pick a tier", options: TIERS, onChange: f.onTier } : null,
    region: tier && f.showRegionStep ? { label: "Region", value: region?.value, placeholder: "All regions", options: f.regionOptions, onChange: f.onRegion } : null,
    body: tier
      ? { label: college ? "Governing Body" : "League", value: body?.value, placeholder: college ? "All governing bodies" : "All leagues", options: f.bodyOptions, onChange: f.onBody }
      : null,
    subDivision: f.showSubDivisionStep
      ? { label: "League / Circuit", value: subDivision?.value, placeholder: `All ${body?.name || ""} divisions`, options: f.subDivisionOptions, onChange: f.onSubDivision }
      : null,
    classDivision: f.showClassStep
      ? { label: "Class", value: classDivision?.value, placeholder: "All classes", options: f.classOptions.map((c) => ({ value: c.name, name: c.name })), onChange: f.onClassDivision }
      : null,
    conference: f.showConferenceStep
      ? { label: "Conference", value: conference?.value, placeholder: "All conferences", groups: NCAA_CONFERENCE_GROUPS, onChange: f.onConference }
      : null,
  };
};
